import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import Brand from "@/components/Brand";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lightning, Briefcase } from "@phosphor-icons/react";

export default function CandidateSignup() {
    const { setUser } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const targetJobId = params.get("job");
    const redirectTo = params.get("redirect") || null;
    const refCode = (params.get("ref") || "").trim().toUpperCase().slice(0, 12) || null;
    const refSrc = (params.get("src") || "").trim().toLowerCase().slice(0, 20) || null;
    const prefillEmail = (params.get("email") || "").trim().toLowerCase().slice(0, 254);
    const prefillPhone = (params.get("phone") || "").replace(/[^\d+]/g, "").slice(0, 16);
    const [targetJob, setTargetJob] = useState(null);
    const [form, setForm] = useState({
        name: (params.get("name") || "").slice(0, 120), email: prefillEmail, password: "", phone: prefillPhone,
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [consentDocs, setConsentDocs] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    // If user landed here from a specific job link, fetch it to show context
    useEffect(() => {
        if (!targetJobId) return;
        api
            .get(`/public/jobs/${targetJobId}`)
            .then((r) => setTargetJob(r.data))
            .catch(() => setTargetJob(null));
    }, [targetJobId]);

    // Fetch current T&C + Privacy versions so we can echo them back to the
    // server — the record then has the exact versions the user saw.
    useEffect(() => {
        api
            .get("/legal/consent-docs")
            .then((r) => setConsentDocs(r.data))
            .catch(() => setConsentDocs(null));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!acceptTerms) {
            setErr("Please accept the Terms of Service and Privacy Policy to continue.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                accept_terms: true,
                accepted_terms_version: consentDocs?.terms?.version || null,
                accepted_privacy_version: consentDocs?.privacy?.version || null,
                ...(refCode ? { ref: refCode, ...(refSrc ? { src: refSrc } : {}) } : {}),
            };
            const { data } = await api.post("/auth/candidate/register", payload);
            // Google Ads — fire the candidate signup conversion on success.
            try {
                const { fireGadsConversion } = await import("@/lib/gtagConversions");
                fireGadsConversion("candidate_signup");
            } catch { /* gtag helper is best-effort */ }
            // Navigate FIRST (replace history), then set user state. This avoids the
            // PublicOnly redirect race where setUser triggers a re-render that
            // bounces the user back to /candidate before nav() runs.
            // If they came from a specific job, deep-link them straight to the
            // apply page so the next click finishes the application.
            // V1.8.6 — first pass the user through the optional
            // "welcome-photo" screen so we capture a profile photo early
            // (or they can skip in one tap).
            const originalDest = targetJobId
                ? `/apply/${targetJobId}`
                : (redirectTo || "/candidate/profile");
            const dest = `/candidate/welcome-photo?next=${encodeURIComponent(originalDest)}`;
            nav(dest, { replace: true });
            setUser(data);
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-paper" data-testid="candidate-signup-page">
            <div className="w-full lg:w-2/3 flex items-center justify-center px-6 sm:px-12 py-12 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-12">
                        <Brand size="h-9" />
                    </div>

                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                        For candidates · Track your applications
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Get hired <br />
                        <span className="text-cobalt">faster.</span>
                    </h1>
                    <p className="text-[#525252] mb-10">
                        One profile, every application, instant AI match scores. Your CV. Every shortlist.
                    </p>

                    {refCode && (
                        <div
                            data-testid="signup-ref-banner"
                            className="mb-6 bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-start gap-3"
                        >
                            <span className="text-lg" role="img" aria-hidden>🎁</span>
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 mb-0.5">
                                    Referral code applied
                                </p>
                                <p className="text-xs text-emerald-800">
                                    You were referred by <span className="font-bold font-mono">{refCode}</span>. When you get hired, your referrer earns a bounty.
                                </p>
                            </div>
                        </div>
                    )}

                    {targetJob && (
                        <div
                            data-testid="signup-target-job"
                            className="mb-8 bg-cobalt/5 border border-cobalt/30 p-4 flex items-start gap-3"
                        >
                            <div className="w-9 h-9 bg-cobalt text-white flex items-center justify-center shrink-0">
                                <Briefcase size={16} weight="bold" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1">
                                    Applying for
                                </p>
                                <p className="font-bold text-ink truncate">{targetJob.title}</p>
                                <p className="text-xs text-[#525252] truncate">
                                    {targetJob.client?.name ? `${targetJob.client.name} · ` : ""}
                                    {targetJob.location || "Remote"}
                                </p>
                                <p className="text-[11px] text-[#525252] mt-2">
                                    Finish signup and we&apos;ll take you straight to the application form.
                                </p>
                            </div>
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                        {prefillPhone && (
                            <p className="text-xs text-[#525252] border border-[#E5E5E5] bg-white px-3 py-2" data-testid="candidate-signup-phone-prefill">
                                Mobile <span className="font-bold text-ink">{prefillPhone}</span> will be linked to this account.
                            </p>
                        )}
                        {["name", "email", "password"].map((k) => (
                            <div key={k}>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    {k}
                                </label>
                                {k === "password" ? (
                                    <PasswordInput
                                        data-testid={`candidate-signup-${k}-input`}
                                        value={form[k]}
                                        required
                                        minLength={6}
                                        autoComplete="new-password"
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                    />
                                ) : (
                                    <input
                                        data-testid={`candidate-signup-${k}-input`}
                                        type={k === "email" ? "email" : "text"}
                                        value={form[k]}
                                        required
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                    />
                                )}
                            </div>
                        ))}

                        {err && (
                            <div
                                data-testid="candidate-signup-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                            >
                                {err}
                            </div>
                        )}

                        <label
                            data-testid="candidate-signup-terms-label"
                            className="flex items-start gap-3 py-2 cursor-pointer select-none"
                        >
                            <input
                                data-testid="candidate-signup-terms-checkbox"
                                type="checkbox"
                                checked={acceptTerms}
                                onChange={(e) => setAcceptTerms(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-cobalt cursor-pointer"
                                required
                            />
                            <span className="text-xs text-[#525252] leading-relaxed">
                                I&apos;m 18+ and I agree to NaukriDaddy&apos;s{" "}
                                <Link
                                    to="/legal/terms"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="candidate-signup-terms-link"
                                    className="font-bold text-cobalt hover:text-cobalt-dark underline underline-offset-2"
                                >
                                    Terms of Service
                                </Link>{" "}
                                and{" "}
                                <Link
                                    to="/legal/privacy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="candidate-signup-privacy-link"
                                    className="font-bold text-cobalt hover:text-cobalt-dark underline underline-offset-2"
                                >
                                    Privacy Policy
                                </Link>
                                . I understand my profile will be shown to recruiters on
                                jobs I apply to, and my resume will be processed by AI to
                                compute match scores.
                                {consentDocs?.terms?.version && (
                                    <span className="block mt-1 text-[10px] text-[#A3A3A3] font-mono">
                                        T&amp;C {consentDocs.terms.version} · Privacy {consentDocs.privacy?.version}
                                    </span>
                                )}
                            </span>
                        </label>

                        <button
                            data-testid="candidate-signup-submit-button"
                            disabled={loading || !acceptTerms}
                            type="submit"
                            className="btn-gradient w-full px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Creating account…" : "Create candidate account"}{" "}
                            <ArrowRight size={16} weight="bold" />
                        </button>
                    </form>

                    <p className="mt-8 text-sm text-[#525252]">
                        Already have a candidate account?{" "}
                        <Link
                            data-testid="goto-candidate-login-from-signup"
                            to={`/candidate/login${redirectTo ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`}
                            className="text-cobalt font-bold underline underline-offset-4"
                        >
                            Sign in
                        </Link>
                    </p>
                    <p className="mt-2 text-xs text-[#A3A3A3]">
                        Recruiter?{" "}
                        <Link
                            to="/login"
                            data-testid="signup-switch-to-recruiter"
                            className="text-ink font-bold underline underline-offset-2 hover:text-cobalt"
                        >
                            Recruiter sign in →
                        </Link>
                    </p>
                </div>
            </div>
            <div className="hidden lg:flex w-1/3 bg-ink text-white p-12 flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    Built for jobseekers
                </p>
                <p className="font-heading font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                    <span className="bg-[#FDE047] text-ink px-2 -mx-1 inline-block">
                        NaukriDaddy
                    </span>
                    ,<br />
                    <span className="text-cobalt">delivered.</span>
                </p>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    © {new Date().getFullYear()} — NaukriDaddy.com
                </div>
            </div>
        </div>
    );
}
